import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalBasic } from "../../../shared/components/ModalBasic";
import { FileList } from "./Components/FileList";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { fetchAllStorageObjects } from "../../../app/state/storageObjectSlice/actions/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { filteredItemsByFileType } from "./helpers";
import { TModalsIds } from "../../../app/state/modalSlice/types";
import { useModalSelectors } from "../../../app/state/modalSlice/hook/use-modal-selectors";
import styles from "./styles.module.css";

interface Props {
  extensions: string[];
  onClose: (id: TModalsIds) => void;
  handleClick: (files: { hash: string; filename: string }[]) => void;
  multiple: boolean;
  className?: string;
}

export const AttachedFileToDatabaseModal = ({
  extensions,
  onClose,
  handleClick,
  multiple,
  className,
}: Props) => {
  const dispatch = useAppDispatch();
  const attachedFileToDatabaseModal = useModalSelectors(
    "attached_file_to_database_modal",
  );
  const { menuItems } = useStorageSelectors("userfiles");
  const { t } = useTranslation();

  const [selectedFiles, setSelectedFiles] = useState<
    Array<{ hash: string; filename: string }>
  >([]);

  useEffect(() => {
    if (attachedFileToDatabaseModal.isOpen) {
      dispatch(fetchAllStorageObjects({ service: "userfiles" }));
    }
  }, [attachedFileToDatabaseModal.isOpen, dispatch]);

  const handleSingleFileClick = (hash: string, filename: string) => {
    handleClick([{ hash, filename }]);
    onClose("attached_file_to_database_modal");
  };

  const handleOk = () => {
    handleClick(selectedFiles);
    onClose("attached_file_to_database_modal");
  };

  const handleCancel = () => {
    onClose("attached_file_to_database_modal");
  };

  const documentFiles = useMemo(
    () => filteredItemsByFileType(extensions, menuItems),
    [menuItems, extensions],
  );

  return (
    <ModalBasic
      open={attachedFileToDatabaseModal.isOpen}
      closeModal={() => onClose("attached_file_to_database_modal")}
      onClose={() => onClose("attached_file_to_database_modal")}
      hasHeader
      title={t("common.choose_the_file")}
      onOk={multiple ? handleOk : undefined}
      buttonOkText={t("common.confirm_button")}
      onCancel={multiple ? handleCancel : undefined}
      hasCloseButton
      hasFooter={multiple}
    >
      <div className={styles["modal-body"]}>
        <FileList
          files={documentFiles}
          multiple={multiple}
          onFileClick={handleSingleFileClick}
          onSelectionChange={multiple ? setSelectedFiles : undefined}
        />
      </div>
    </ModalBasic>
  );
};
